<script setup lang="ts">
import type { Schemas } from '#shopware'
import { ApiClientError } from '@shopware/api-client'

interface SidenavCartProps {
    showCloseButton?: boolean
    maxHeight?: string // Used as 'max-height' style property
    emptyCartMessage?: string
}

const props = withDefaults(defineProps<SidenavCartProps>(), {
    showCloseButton: true,
    maxHeight: 'calc(100vh - 200px)',
    emptyCartMessage: ''
})

// Global sidenav state management
const { isOpen } = useSidenav()
const cartOpen = isOpen('cart')

// Cart product-added notification
const { isVisible: notificationVisible } = useCartProductNotification()

// i18n
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

// Initialize data and state
const { cart, refreshCart, changeProductQuantity, removeItem, totalProductQuantity } = useCart()
const { resolveApiErrors } = useApiErrorsResolver()
const { pushError } = useNotifications()

// Get Nuxt app instance
const nuxtApp = useNuxtApp()

// Navigate to checkout page and close sidenav
const handleCheckout = () => {
    navigateTo(formatLink('/checkout'))
    cartOpen.value = false
}

// Close cart sidenav
const handleCart = () => {
    cartOpen.value = false
}

// Update quantity of a cart item
const onChangeQuantity = async (data: { id: string, quantity: number }) => {
    try {
        await changeProductQuantity({ id: data.id, quantity: data.quantity })
    } catch (e) {
        handleError(e)
    }
}

// Remove an item from cart
const onRemoveItem = async (item: Schemas["LineItem"]) => {
    try {
        // Track remove from cart event before removal
        if (import.meta.client) {
            const price = item.price?.unitPrice || 0

            const payload = item.payload as { manufacturer?: { name?: string }, options?: { option: string }[] } | undefined

            nuxtApp.callHook('nuxtware:cart:removed', {
                product: {
                    id: item.id,
                    name: item.label || '',
                    price: price,
                    brand: payload?.manufacturer?.name,
                    variant: payload?.options?.map((o) => o.option).join(' / ')
                },
                quantity: item.quantity
            })
        }

        await removeItem(item)
    } catch (e) {
        handleError(e)
    }
}

const handleError = (e: unknown) => {
    if (e instanceof ApiClientError) {
        const _errors = resolveApiErrors(e.details.errors)
        for (const error of _errors) {
            pushError(error)
        }
    }
}

// Watch for open/close to fetch data
watch(cartOpen, (newValue) => {
    if (newValue) {
        refreshCart()
    }
})

// Automatically fetch on component mount if opened
onMounted(() => {
    if (cartOpen.value) {
        refreshCart()
    }
})
</script>

<template>
    <SidenavOverlay
        v-model="cartOpen"
        direction="right"
        width-class="w-full md:w-[600px]"
    >
        <div class="flex flex-col h-full bg-base-100">
            <!-- Header -->
            <SidenavHeader
                :title="$t('cart.title')"
                :show-close-button="showCloseButton"
                :close-aria-label="$t('cart.close')"
                @close="cartOpen = false"
            />

            <!-- Product Added Notification -->
            <Transition
                enter-active-class="transition-all duration-200 ease-out"
                leave-active-class="transition-all duration-200 ease-in"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="notificationVisible"
                    class="bg-base-200 px-6 md:px-9 py-6 flex items-center gap-3 border-b border-border"
                    role="alert"
                    aria-live="polite"
                >
                    <FoundationIcon name="check" class="w-5 h-5 flex-shrink-0" />
                    <span class="font-medium">
                        {{ $t('cart.notification.productAdded') }}
                    </span>
                </div>
            </Transition>

            <!-- Empty cart state -->
            <div
                v-if="!cart?.lineItems?.length" 
                class="flex flex-col items-center justify-center flex-grow p-8 bg-base-200"
            >
                <FoundationIcon name="cart" class="w-14 h-14 mb-4 text-base-500" />
                <p class="text-2xl font-bold mb-4 text-center">{{ props.emptyCartMessage || $t('cart.emptyCartLabel') }}</p>

                <FoundationButton
                    color="primary"
                    @click="cartOpen = false"
                >
                    {{ $t('cart.discover') }}
                </FoundationButton>

            </div>

            <!-- Cart items -->
            <div v-else-if="cart?.lineItems?.length" class="overflow-auto" :style="{ 'max-height': props.maxHeight }">
                <div class="border-b border-border px-6 md:px-9 py-3 text-sm font-bold">
                    {{ totalProductQuantity }} {{ $t('cart.products') }}
                </div>

                <div>
                    <CartItem
                        v-for="item in cart.lineItems"
                        :key="item.id"
                        :item="item"
                        class="px-6 md:px-9 py-6 border-b border-border last:border-b-0"
                        @update:quantity="(quantity) => onChangeQuantity({ id: item.id, quantity })"
                        @remove="onRemoveItem(item)"
                    />
                </div>
            </div>
            
            <!-- Cart summary -->
            <div v-if="cart?.lineItems?.length" class="border-t border-border">
                <CartSimpleSummary
                    :cart="cart" 
                    @cart="handleCart"
                    @checkout="handleCheckout"
                    @close="cartOpen = false"
                />
            </div>
        </div>
    </SidenavOverlay>
</template>
